import React from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import classNames from 'classnames';
import { Box, Text, Title } from '@nerdwallet/currency';

import parseAwardsFormatting from './lib/parse-awards-formatting';
import BackgroundColor from '../background-color/background-color';
import getTextColor from '../../lib/text-color-config';

import AwardsNavigationBox from './awards-navigation-box';
import './awards-navigation.less';

import {
  repeatable,
  stringTextAreaSpec,
  stringSelectSpec,
  boolCheckboxSpec,
} from '../../lib/editing-configs';

const sizesEnum = {
  medium: 'large',
  'x-large': 'default',
  large: 'small',
  small: 'small',
  'x-small': 'x-small',
};

const AwardsNavigation = ({
  boxes,
  title,
  titleSize,
  titleTextColor,
  subtitle,
  bgOptions,
  awardsYear,
  titleColorOptions,
  currentCategory,
  shouldTrack,
}) => {
  let TitleSizeComponent = Text;
  if (titleSize === 'large' || titleSize === 'x-large') {
    TitleSizeComponent = Title;
  }
  let colorOptions = { textColor: titleTextColor };
  if (titleColorOptions) {
    colorOptions = titleColorOptions;
  }
  const { textColor } = getTextColor(colorOptions);
  const colorStyle = { color: textColor };

  const navSections = boxes.map((box, index) => (
    <AwardsNavigationBox
      {...box}
      key={`${box.section}`}
      awardsYear={awardsYear}
      index={index}
      currentCategory={currentCategory}
      shouldTrack={shouldTrack}
    />
  ));
  return (
    <BackgroundColor bgOptions={bgOptions}>
      {(backgroundColor) => (
        <div
          className={classNames(['awards-nav__cont', `layout-${awardsYear}`])}
          style={{
            backgroundColor,
          }}
        >
          <Box
            className={classNames([
              'awards-nav__title',
              `layout-${awardsYear}`,
            ])}
            size={titleSize}
            style={colorStyle}
          >
            <TitleSizeComponent size={sizesEnum[titleSize]}>
              {parseAwardsFormatting(title)}
            </TitleSizeComponent>
          </Box>
          {subtitle && subtitle !== '' && (
            <Box className={classNames(['awards-nav__subtitle'])}>
              <Text>{subtitle}</Text>
            </Box>
          )}
          <div
            className={classNames([
              'awards-nav__boxes',
              `layout-${awardsYear}`,
            ])}
          >
            {navSections}
          </div>
        </div>
      )}
    </BackgroundColor>
  );
};

AwardsNavigation.propTypes = {
  boxes: PropTypes.arrayOf(PropTypes.shape(AwardsNavigation.propTypes)),
  title: PropTypes.string,
  titleSize: PropTypes.string,
  subtitle: PropTypes.string,
  titleTextColor: PropTypes.string,
  awardsYear: PropTypes.string,
  shouldTrack: PropTypes.bool,
  ...BackgroundColor.propTypes,
};

AwardsNavigation.getEditingConfig = (componentDocument) => {
  const currentCategoryConfig =
    componentDocument?.props?.awardsYear === '2022'
      ? [
          stringSelectSpec('currentCategory', 'Current Category', [
            {
              label: 'Select a Category',
              value: '',
            },
            ..._.get(componentDocument, 'props.boxes', []).map((item) => ({
              label: _.capitalize(item.section),
              value: item.sectionUrl,
            })),
          ]),
        ]
      : [];
  return [
    stringSelectSpec('awardsYear', 'Awards Year', [
      { label: '2021', value: '2021' },
      { label: '2022', value: '2022' },
      { label: '2023', value: '2023' },
      { label: '2020', value: '2020' },
      { label: 'Inclusion & Diversity', value: 'inclusion-diversity' },
    ]),
    ...BackgroundColor.getEditingConfig(componentDocument),
    ...getTextColor.getEditingConfig(
      componentDocument,
      'titleColorOptions',
      'Title Text Color'
    ),
    stringTextAreaSpec('title', 'Title', {
      help: 'For yellow Awards 2021 text, surround title text with * like *this*. For italizices Chronicle font styling, surround title text with | like |this|. For Gotham Black font styling, surround title text with + like +this+',
    }),
    stringSelectSpec(
      'titleSize',
      'Title size',
      [
        {
          value: 'medium',
          label: 'Medium (default)',
        },
        {
          value: 'x-large',
          label: 'X-large',
        },
        {
          value: 'large',
          label: 'Large',
        },
        {
          value: 'small',
          label: 'Small',
        },
        {
          value: 'x-small',
          label: 'X-Small',
        },
      ],
      {
        help: 'Title size',
      }
    ),
    boolCheckboxSpec(
      'shouldTrack',
      'Add BOA Awards analytics instrumentation?'
    ),
    stringTextAreaSpec('subtitle', 'Subtitle'),
    ...currentCategoryConfig,
    repeatable('boxes', 'Content block', 'Content block', () =>
      AwardsNavigationBox.getEditingConfig(componentDocument)
    ),
  ];
};

AwardsNavigation.defaultProps = {
  boxes: [],
  title: '',
  titleSize: 'medium',
  titleTextColor: 'neutral-darkest',
  ...BackgroundColor.defaultProps,
};

AwardsNavigation.displayName = 'Awards Navigation';

export default AwardsNavigation;
